import logger from './logger';
/**
 * Performance Monitor for Debate Engine Optimizations
 * Tracks API calls, cache hits, and translation efficiency
 */

interface PerformanceMetrics {
  authApiCalls: number;
  authCacheHits: number;
  translationApiCalls: number;
  translationCacheHits: number;
  pusherConnections: number;
  pageLoads: number;
  sessionStart: number;
}

class PerformanceMonitor {
  private metrics: PerformanceMetrics;
  private isProduction: boolean;

  constructor() {
    this.isProduction = process.env.NODE_ENV === 'production';
    this.metrics = this.loadMetrics();
  }

  private loadMetrics(): PerformanceMetrics {
    const saved = sessionStorage.getItem('performance_metrics');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        logger.warn('Failed to parse performance metrics');
      }
    }
    
    return {
      authApiCalls: 0,
      authCacheHits: 0,
      translationApiCalls: 0,
      translationCacheHits: 0,
      pusherConnections: 0,
      pageLoads: 0,
      sessionStart: Date.now()
    };
  }

  private saveMetrics() {
    try {
      sessionStorage.setItem('performance_metrics', JSON.stringify(this.metrics));
    } catch (e) {
      logger.warn('Failed to save performance metrics');
    }
  }

  // Track authentication events
  trackAuthApiCall() {
    this.metrics.authApiCalls++;
    this.saveMetrics();
    if (!this.isProduction) {
      logger.info('📊 Auth API call tracked:', this.metrics.authApiCalls);
    }
  }

  trackAuthCacheHit() {
    this.metrics.authCacheHits++;
    this.saveMetrics();
    if (!this.isProduction) {
      logger.info('📊 Auth cache hit tracked:', this.metrics.authCacheHits);
    }
  }

  // Track translation events
  trackTranslationApiCall() {
    this.metrics.translationApiCalls++;
    this.saveMetrics();
    if (!this.isProduction) {
      logger.info('📊 Translation API call tracked:', this.metrics.translationApiCalls);
    }
  }

  trackTranslationCacheHit() {
    this.metrics.translationCacheHits++;
    this.saveMetrics();
    if (!this.isProduction) {
      logger.info('📊 Translation cache hit tracked:', this.metrics.translationCacheHits);
    }
  }

  // Track Pusher connections
  trackPusherConnection() {
    this.metrics.pusherConnections++;
    this.saveMetrics();
    if (!this.isProduction) {
      logger.info('📊 Pusher connection tracked:', this.metrics.pusherConnections);
    }
  }

  // Track page loads
  trackPageLoad() {
    this.metrics.pageLoads++;
    this.saveMetrics();
    if (!this.isProduction) {
      logger.info('📊 Page load tracked:', this.metrics.pageLoads);
    }
  }

  // Get efficiency ratios
  getAuthEfficiency(): number {
    const total = this.metrics.authApiCalls + this.metrics.authCacheHits;
    if (total === 0) return 0;
    return (this.metrics.authCacheHits / total) * 100;
  }

  getTranslationEfficiency(): number {
    const total = this.metrics.translationApiCalls + this.metrics.translationCacheHits;
    if (total === 0) return 0;
    return (this.metrics.translationCacheHits / total) * 100;
  }

  // Get session summary
  getSessionSummary() {
    const sessionDuration = Date.now() - this.metrics.sessionStart;
    const sessionMinutes = Math.round(sessionDuration / 60000);
    
    return {
      sessionDuration: sessionMinutes,
      authEfficiency: this.getAuthEfficiency(),
      translationEfficiency: this.getTranslationEfficiency(),
      apiCallsPerPage: this.metrics.pageLoads > 0 ? 
        (this.metrics.authApiCalls + this.metrics.translationApiCalls) / this.metrics.pageLoads : 0,
      metrics: { ...this.metrics }
    };
  }

  // Log performance summary (for debugging)
  logSummary() {
    if (this.isProduction) return;
    
    const summary = this.getSessionSummary();
    console.group('📊 Performance Summary');
    logger.info(`Session Duration: ${summary.sessionDuration} minutes`);
    logger.info(`Auth Efficiency: ${summary.authEfficiency.toFixed(1)}% cache hits`);
    logger.info(`Translation Efficiency: ${summary.translationEfficiency.toFixed(1)}% cache hits`);
    logger.info(`API Calls per Page: ${summary.apiCallsPerPage.toFixed(1)}`);
    logger.info('Raw Metrics:', summary.metrics);
    console.groupEnd();
  }

  // Reset metrics (useful for testing)
  reset() {
    this.metrics = {
      authApiCalls: 0,
      authCacheHits: 0,
      translationApiCalls: 0,
      translationCacheHits: 0,
      pusherConnections: 0,
      pageLoads: 0,
      sessionStart: Date.now()
    };
    this.saveMetrics();
    logger.info('📊 Performance metrics reset');
  }
}

// Singleton instance
export const performanceMonitor = new PerformanceMonitor();

// Auto-log summary every 5 minutes in development
if (process.env.NODE_ENV === 'development') {
  setInterval(() => {
    performanceMonitor.logSummary();
  }, 5 * 60 * 1000);
}

export default performanceMonitor; 